// src/context/StudentAuthContext.js
//
// The student session for Kuestion Campus (contract §9.3). Deliberately
// separate from AuthContext: a student token is a different JWT audience,
// students have no settings, no billing and no idle-timeout preference, and
// the two sessions must never be confused for one another.
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from '../config/apiConfig';
import { storageKey } from '../config/routes';

const StudentAuthContext = createContext(null);
// Namespaced with the rest of the Kuestion area (Separation v2.1 §3).
const SESSION_KEY = storageKey('kuestion', 'student');
const LEGACY_KEY = 'kq_student_session';

const readSession = () => {
  try {
    // Carry a session the previous build kept in localStorage into
    // sessionStorage once (storage contract §4). Then only sessionStorage.
    const carried = localStorage.getItem(SESSION_KEY);
    if (carried) { if (!sessionStorage.getItem(SESSION_KEY)) sessionStorage.setItem(SESSION_KEY, carried); localStorage.removeItem(SESSION_KEY); }
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      if (!sessionStorage.getItem(SESSION_KEY)) sessionStorage.setItem(SESSION_KEY, legacy);
      localStorage.removeItem(LEGACY_KEY);
    }
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token ? parsed : null;
  } catch {
    return null;
  }
};

export const StudentAuthProvider = ({ children }) => {
  const [student, setStudent] = useState(readSession);

  // POST /api/campus/student/login. A failed login is `invalid_credentials`
  // whether the ID was unknown, the password wrong, or the account inactive —
  // the backend keeps those indistinguishable on purpose, and so do we.
  // The login ID is sent verbatim: the server normalises case (B4).
  const login = useCallback(async (loginId, password) => {
    let res;
    try {
      res = await fetch(API_ENDPOINTS.CAMPUS_STUDENT_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_id: String(loginId).trim(), password }),
      });
    } catch {
      throw new Error('network_error');
    }
    let data = null;
    try { data = await res.json(); } catch { /* not JSON */ }
    if (!res.ok || !data || data.status !== 'success') {
      throw new Error(data?.reason || 'invalid_credentials');
    }
    const session = {
      token: data.data.token,
      ...data.data.student,
      _since: Date.now(),
    };
    setStudent(session);
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* private mode */ }
    return session;
  }, []);

  const logout = useCallback(() => {
    setStudent(null);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* private mode */ }
  }, []);

  const value = useMemo(() => ({
    student,
    token: student?.token || null,
    isStudent: Boolean(student),
    login,
    logout,
  }), [student, login, logout]);

  return (
    <StudentAuthContext.Provider value={value}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error('useStudentAuth must be used within a StudentAuthProvider');
  return ctx;
};

// For shared components (the login form) that render in both areas: null
// outside the Kuestion area instead of throwing.
export const useStudentAuthOptional = () => useContext(StudentAuthContext);

export default StudentAuthContext;
